import { createSelector } from '@reduxjs/toolkit';

import { IComment, IPost, ISpecificPostState, IStore2, IUserDetails } from 'types';
import { getCommentById } from '../post/helper';

const specificPostSelector = (state: IStore2) => state.specificPost;

const mainPostIdSelector = (state: IStore2) => state.specificPost.id;

const selectedPostIdSelector = (state: IStore2) => state.specificPost.selectedPostId;

const subPostsSelector = (state: IStore2) => state.specificPost.subPosts;

const selectedCommentIdSelector = (state: IStore2) => state.specificPost.selectedCommentId;

const selectedReplyIdSelector = (state: IStore2) => state.specificPost.selectedReplyId;

const commentIdSelector = (state: IStore2) => state.popup.commentId;

const replyIdSelector = (state: IStore2) => state.popup.replyId;

const getSelectedPost = (state: ISpecificPostState) => {
  const { subPosts, selectedPostId } = state;

  const subPost = subPosts.find((item) => item.id === selectedPostId);

  return subPost || state;
};

const selectedSpecificPostSelector = createSelector(
  [selectedPostIdSelector, specificPostSelector],
  (selectedPostId, post): IPost | undefined => {
    const { id, userDetails, subPosts, actionId, shouldHideDate } = post;

    if (!userDetails) {
      return undefined;
    }

    const selectedPost: IPost = {
      actionId,
      text: post.text,
      isCommentsExpanded: true,
      images: post.images,
      mojoDetails: post.mojoDetails,
      action: null,
      userDetails,
      productDetails: post.productDetails,
      imageComposition: post.imageComposition,
      isEditable: post.isEditable,
      lastEdited: post.lastEdited,
      isFollowing: post.isFollowing,
      shopId: post.shopId,
      repostDetails: post.repostDetails,
      id,
      isLiked: post.isLiked,
      isReposted: post.isReposted,
      likes: post.likes,
      totalLikes: post.totalLikes,
      totalComments: post.totalComments,
      totalReposts: post.totalReposts,
      comments: post.comments,
      selfComments: [],
      lastComment: null,
      taggedUsers: post.taggedUsers,
      shouldDisplayHideFromActivity: false,
      isShoppingPost: post.isShoppingPost,
      shouldHideDate: !!shouldHideDate,
      externalPost: null,
    };

    const selectedSubPost = subPosts?.find((item) => item.id === selectedPostId);

    if (selectedSubPost) {
      return { ...selectedPost, ...selectedSubPost };
    }

    return selectedPost;
  },
);

const selectedCommentSelector = createSelector(
  [specificPostSelector, selectedCommentIdSelector, selectedReplyIdSelector],
  (state, selectedCommentId, replyId): IComment | undefined => {
    const post = getSelectedPost(state);

    let comment = post?.comments.list?.find((item) => item.id === selectedCommentId);

    if (replyId) {
      comment = comment?.replies.find((item) => item.id === replyId);
    }

    return comment;
  },
);

const subPostSelector = createSelector(
  [specificPostSelector, selectedPostIdSelector],
  (state, selectedPostId): IPost | null => {
    const {
      subPosts,
      images,
      shopId,
      isFollowing,
      text,
      userDetails,
      imageComposition,
      isEditable,
      lastEdited,
      actionId,
      taggedUsers,
      shouldHideDate,
    } = state;

    const post = subPosts.find((item) => item.id === selectedPostId);

    if (!post || !userDetails) {
      return null;
    }

    const { id, isLiked, likes, totalLikes, totalComments, comments } = post;

    return {
      actionId,
      shopId,
      id,
      taggedUsers,
      userDetails,
      action: null,
      repostDetails: null,
      isCommentsExpanded: true,
      isReposted: false,
      totalReposts: 0,
      images,
      mojoDetails: null,
      text,
      imageComposition,
      selfComments: [],
      isFollowing,
      comments,
      totalComments,
      totalLikes,
      likes,
      isLiked,
      lastComment: null,
      productDetails: null,
      isShoppingPost: false,
      isEditable,
      lastEdited,
      shouldDisplayHideFromActivity: false,
      shouldHideDate: !!shouldHideDate,
      externalPost: null,
    };
  },
);

const mainPostSelector = createSelector([specificPostSelector], (post): IPost | null => {
  if (!post.userDetails || !post.id) {
    return null;
  }

  return {
    actionId: post.actionId,
    userDetails: post.userDetails,
    isCommentsExpanded: true,
    action: post.action,
    shopId: post.shopId,
    taggedUsers: post.taggedUsers,
    id: post.id,
    images: post.images,
    mojoDetails: post.mojoDetails,
    repostDetails: post.repostDetails,
    imageComposition: post.imageComposition,
    backgroundIndex: post.backgroundIndex,
    text: post.text,
    isFollowing: post.isFollowing,
    comments: post.comments,
    productDetails: post.productDetails,
    selfComments: [],
    totalComments: post.totalComments,
    totalLikes: post.totalLikes,
    totalReposts: post.totalReposts,
    likes: post.likes,
    isLiked: post.isLiked,
    isReposted: post.isReposted,
    isShoppingPost: post.isShoppingPost,
    lastComment: null,
    lastEdited: post.lastEdited,
    isEditable: post.isEditable,
    shouldDisplayHideFromActivity: false,
    shouldHideDate: !!post.shouldHideDate,
    externalPost: post.externalPost,
  };
});

const getLikeListSelector = createSelector(
  [selectedSpecificPostSelector, commentIdSelector, replyIdSelector],
  (post, commentId, replyId): IUserDetails[] | null => {
    if (!post) return null;

    let selectedComment;
    if (commentId) {
      selectedComment = getCommentById(post, commentId, replyId);
    }

    if (selectedComment) return selectedComment.likes.list;

    return post.likes.list;
  },
);

const getTotalLikesSelector = createSelector(
  [selectedSpecificPostSelector, commentIdSelector, replyIdSelector],
  (post, commentId, replyId): number => {
    if (!post) return 0;

    let selectedComment;

    if (commentId) {
      selectedComment = getCommentById(post, commentId, replyId);
    }

    if (selectedComment) return selectedComment.totalLikes;

    return post.totalLikes;
  },
);

const hasMoreLikesSelector = createSelector(
  [selectedSpecificPostSelector, commentIdSelector, replyIdSelector],
  (post, commentId, replyId): boolean => {
    if (!post) return false;

    let selectedComment;
    if (commentId) {
      selectedComment = getCommentById(post, commentId, replyId);
    }

    return selectedComment?.likes.hasMore || post.likes.hasMore;
  },
);

export const SpecificPostSelector = {
  selectedSpecificPostSelector,
  selectedPostIdSelector,
  subPostsSelector,
  specificPostSelector,
  selectedCommentSelector,
  mainPostSelector,
  mainPostIdSelector,
  subPostSelector,
  getLikeListSelector,
  getTotalLikesSelector,
  hasMoreLikesSelector,
};
